import { useState, useCallback, useRef, useEffect } from 'react';
import { cartFunctions, isValidRawCart, updateCartCountInHeader } from '../utils/promo-cart-utils';
import { useNewRelicLogging } from './useNewRelicLogging';

const { fetchCartHelper, updateCartHelper, addToCartHelper } = cartFunctions;

export const useCart = ({ initializeCall = true }) => {
  const isMounted = useRef(false);
  const [currentCartModel, setCurrentCartModel] = useState();
  const [frozenCartModel, setFrozenCartModel] = useState();
  // If a user adds/updates items in cart before initial cart call comes back
  const initialFetchComplete = useRef(false);
  const [isCartLoading, setIsCartLoading] = useState(false);

  const handleReturnedCartModel = (newCartModel) => {
    if (isMounted.current) {
      initialFetchComplete.current = true;
      setCurrentCartModel(newCartModel);
    }
  };

  const { sendErrorsToNewRelic, sendDataToNewRelic } = useNewRelicLogging();

  useEffect(() => {
    isMounted.current = true;
    // listen to generic add to cart events and handle the results
    const handleAtcSuccess = (cartSuccessEvent) => {
      if (cartSuccessEvent?.eventName === 'success' && isValidRawCart(cartSuccessEvent.output?.CartModel)) {
        handleReturnedCartModel(cartSuccessEvent.output.CartModel);
      }
    };

    window.LIFE_CYCLE_EVENT_BUS.on('add-to-cart.success', handleAtcSuccess);

    return () => {
      isMounted.current = false;
      window.LIFE_CYCLE_EVENT_BUS.off('add-to-cart.success', handleAtcSuccess);
    };
  }, []);

  useEffect(() => {
    if (initializeCall && !initialFetchComplete.current) {
      setIsCartLoading(true);
      fetchCartHelper()
        .then((newCartModel) => {
          // Doesn't allow cart to be overwritten if we have already received data.
          if (!initialFetchComplete.current) {
            handleReturnedCartModel(newCartModel);
          }
        })
        .finally(() => {
          if (isMounted.current) setIsCartLoading(false);
        });
    }
  }, [initializeCall]);

  // should always have currentCartModel in dep array
  useEffect(() => {
    if (!frozenCartModel) setIsCartLoading(false);
  }, [currentCartModel, frozenCartModel]);

  const fetchCart = useCallback(() => {
    if (isMounted.current) {
      setIsCartLoading(true);
      try {
        return fetchCartHelper()
          .then((newCartModel) => {
            handleReturnedCartModel(newCartModel);
            const props = { actionName: 'PromotionProductsCart', callType: 'fetchCart' };
            sendDataToNewRelic(props);
            return newCartModel;
          })
          .finally(() => {
            if (isMounted.current) setIsCartLoading(false);
          });
      } catch (error) {
        const props = { actionName: 'PromotionProductsCartError', errorMessage: error.message, callType: 'fetchCart' };
        sendErrorsToNewRelic(props);
        setIsCartLoading(false);
        return Promise.reject(error);
      }
    }

    return Promise.reject(new Error('useCart is unmounted'));
  }, [sendErrorsToNewRelic, sendDataToNewRelic]);

  const addToCart = useCallback((cartRequest) => {
    setIsCartLoading(true);
    try {
      return addToCartHelper(cartRequest)
        .then((newCartModel) => {
          handleReturnedCartModel(newCartModel);
          updateCartCountInHeader(newCartModel);
          window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-cart.success', { CartModel: newCartModel });
          const props = { actionName: 'PromotionProductsCart', callType: 'addToCart' };
          sendDataToNewRelic(props);
          if (isMounted.current) setIsCartLoading(false);

          return newCartModel;
        })
        .catch((error) => {
          const props = { actionName: 'PromotionProductsCartError',
            errorMessage: error.message,
            callType: 'addToCart' };
          sendErrorsToNewRelic(props);
          return fetchCart();
        });
    } catch (error) {
      const props = { actionName: 'PromotionProductsCartError', errorMessage: error.message, callType: 'addToCart' };
      sendErrorsToNewRelic(props);
      setIsCartLoading(false);
      return Promise.reject(error);
    }

  }, [fetchCart, sendErrorsToNewRelic, sendDataToNewRelic]);

  const updateCart = useCallback((cartRequest) => {
    setIsCartLoading(true);
    try {
      return updateCartHelper(cartRequest)
        .then((newCartModel) => {
          handleReturnedCartModel(newCartModel);
          updateCartCountInHeader(newCartModel);
          window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-cart.success', { CartModel: newCartModel });
          const props = { actionName: 'PromotionProductsCart', callType: 'update' };
          sendDataToNewRelic(props);
          if (isMounted.current) setIsCartLoading(false);

          return newCartModel;
        })
        .catch((error) => {
          const props = { actionName: 'PromotionProductsCartError', errorMessage: error.message, callType: 'update' };
          sendErrorsToNewRelic(props);

          return fetchCart();
        });
    } catch (error) {
      const props = { actionName: 'PromotionProductsCartError', errorMessage: error.message, callType: 'update' };
      sendErrorsToNewRelic(props);
      setIsCartLoading(false);
      return Promise.reject(error);

    }

  }, [fetchCart, sendErrorsToNewRelic, sendDataToNewRelic]);

  // when consumer calls freezeCart it takes the current value of cart and stores it in state,
  // it also returns a function that will "unfreeze" the cart by resetting it to null
  const freezeCart = useCallback(() => {
    if (isMounted.current) setFrozenCartModel(currentCartModel);

    return () => {
      if (isMounted.current) setFrozenCartModel(null);
    };
  }, [currentCartModel]);

  return {
    // if cart is "frozen" return the frozen value of cart, otherwise return the latest
    CartModel: frozenCartModel || currentCartModel,
    addToCart,
    updateCart,
    isCartLoading,
    setIsCartLoading,
    freezeCart
  };
};
